// src/hooks/useAiStream.js
import { useState, useCallback, useRef } from 'react';
import { supabaseUrl, supabaseAnonKey } from '../config/supabaseConfig';
import { supabase } from '../supabase';
import { beginWrite } from '../utils/app/pendingWrites';
import { parseAssistantStreamAccumulated } from '../utils/ai/parseAssistantStreamAccumulated';
import { errorFromAiFetchResponse, errorFromAiInvoke } from '../utils/ai/normalizeAiError';

/**
 * useAiStream — SSE consumer for the ai-assistant edge function.
 *
 * Returns streaming state and a startStream function.
 * Falls back to supabase.functions.invoke if SSE fails.
 */
export default function useAiStream() {
  const [streamingText, setStreamingText] = useState('');
  const [streamingAction, setStreamingAction] = useState(null);
  const [streamingContext, setStreamingContext] = useState(null);
  const [streamingTranscript, setStreamingTranscript] = useState(null);
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamPhase, setStreamPhase] = useState('idle'); // idle | context | text | action | done
  const abortRef = useRef(null);

  const reset = useCallback(() => {
    setStreamingText('');
    setStreamingAction(null);
    setStreamingContext(null);
    setStreamingTranscript(null);
    setIsStreaming(false);
    setStreamPhase('idle');
  }, []);

  const abort = useCallback(() => {
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
    setIsStreaming(false);
  }, []);

  /**
   * Start an SSE stream to ai-assistant.
   * @param {Object|FormData} body — request body (JSON object or FormData for voice)
   * @returns {Promise<{ text: string, action: object|null, context: object|null, transcript: string|null } | null>}
   *          Resolves with the final accumulated data, or null if aborted.
   */
  const runStream = useCallback(async (body) => {
    // Abort any in-flight stream
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    reset();
    setIsStreaming(true);

    // Get auth token
    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token;
    if (!token) {
      setIsStreaming(false);
      // A6: throw so downstream catch blocks can surface feedback via setErrorFromException
      throw new Error('auth_required');
    }

    const isFormData = body instanceof FormData;
    const headers = {
      Authorization: `Bearer ${token}`,
      Accept: 'text/event-stream',
      apikey: supabaseAnonKey || '',
    };
    if (!isFormData) {
      headers['Content-Type'] = 'application/json';
    }

    const url = `${supabaseUrl}/functions/v1/ai-assistant`;

    let accText = '';
    let accAction = null;
    let accContext = null;
    let accTranscript = null;
    // Server tells us, via a `parse_error` SSE event, when the model returned malformed
    // JSON it couldn't recover. Used as the final fallback text if our client-side
    // rescue parser also fails — never let users see raw JSON or empty bubbles.
    let serverParseErrorMessage = null;

    try {
      const res = await fetch(url, {
        method: 'POST',
        headers,
        body: isFormData ? body : JSON.stringify(body),
        signal: controller.signal,
      });

      // Fallback: if server returned JSON instead of SSE
      const contentType = res.headers.get('content-type') || '';
      if (contentType.includes('application/json')) {
        const data = await res.json();
        if (data.success && data.text) {
          setStreamingText(data.text);
          setStreamingAction(data.action || null);
          setStreamPhase('done');
          setIsStreaming(false);
          return { text: data.text, action: data.action || null, context: null, transcript: data.transcript || null };
        }
        const err = new Error(data.error?.message || data.text || 'Request failed');
        if (data.error?.code) err.code = data.error.code;
        err.status = res.status;
        throw err;
      }

      if (!res.ok) {
        // Preserve server error code (e.g. AI_LICENSE_BLOCKED) when the body
        // isn't application/json (or got returned as text/event-stream for an error).
        throw await errorFromAiFetchResponse(res, `Stream failed: ${res.status}`);
      }
      if (!res.body) {
        throw new Error(`Stream failed: ${res.status}`);
      }

      // Parse SSE stream
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        let currentEvent = '';
        for (const line of lines) {
          if (line.startsWith('event: ')) {
            currentEvent = line.slice(7).trim();
          } else if (line.startsWith('data: ') && currentEvent) {
            try {
              const payload = JSON.parse(line.slice(6));
              switch (currentEvent) {
                case 'context':
                  accContext = payload;
                  setStreamingContext(payload);
                  setStreamPhase('context');
                  break;
                case 'transcript':
                  accTranscript = payload.text || '';
                  setStreamingTranscript(payload.text || '');
                  break;
                case 'text':
                  // Model streams JSON-shaped output; never mirror raw tokens into UI (users must not see JSON).
                  // The typing indicator handles the live feedback; final parsed text is set after the stream ends.
                  accText += payload.token || '';
                  setStreamPhase('text');
                  break;
                case 'action':
                  accAction = payload;
                  setStreamingAction(payload);
                  setStreamPhase('action');
                  break;
                case 'parse_error':
                  // Server couldn't decode the model's JSON. Stash the friendly
                  // message; we'll only surface it if our own rescue parser also fails.
                  serverParseErrorMessage = payload.message || null;
                  break;
                case 'done':
                  setStreamPhase('done');
                  break;
                case 'error': {
                  const sseErr = new Error(payload.message || 'Stream error');
                  if (payload.code) sseErr.code = payload.code;
                  throw sseErr;
                }
                default:
                  break;
              }
            } catch (parseErr) {
              if (parseErr.message && parseErr.message !== 'Stream error') {
                throw parseErr;
              }
            }
            currentEvent = '';
          }
        }
      }

      const parsed = parseAssistantStreamAccumulated(accText);
      const displayText = parsed.text || serverParseErrorMessage || '';
      const finalAction = accAction ?? parsed.action ?? null;
      setStreamingAction(finalAction);
      // Do not put final text into streaming state — the chat message list renders the reply; avoids any flash.
      setStreamingText('');

      setIsStreaming(false);
      return {
        text: displayText,
        action: finalAction,
        context: accContext,
        transcript: accTranscript,
      };

    } catch (err) {
      if (controller.signal.aborted) return null;

      // If the server already answered with a structured code (e.g. an AI
      // enforcement error), retrying via invoke is pointless — rethrow and
      // preserve the code so downstream classification can branch.
      if (err && typeof err === 'object' && typeof err.code === 'string') {
        setIsStreaming(false);
        throw err;
      }

      // Fallback to invoke if streaming failed entirely
      try {
        const invokeResult = await supabase.functions.invoke('ai-assistant', { body });
        const { data, error } = invokeResult;
        if (error || !data?.success) {
          throw await errorFromAiInvoke(invokeResult);
        }
        setStreamingText(data.text || '');
        setStreamingAction(data.action || null);
        setStreamPhase('done');
        setIsStreaming(false);
        return { text: data.text, action: data.action || null, context: null, transcript: data.transcript || null };
      } catch (fallbackErr) {
        setIsStreaming(false);
        throw fallbackErr;
      }
    }
  }, [reset]);

  // KAN-886: a live stream (and its invoke fallback) blocks the silent app update.
  const startStream = useCallback(async (body) => {
    const end = beginWrite();
    try {
      return await runStream(body);
    } finally {
      end();
    }
  }, [runStream]);

  return {
    streamingText,
    streamingAction,
    streamingContext,
    streamingTranscript,
    isStreaming,
    streamPhase,
    startStream,
    abortStream: abort,
    resetStream: reset,
  };
}
